/**
 * Decision 0143 (observability phase 1): the one Analytics Engine dataset `minga_events` (binding `MINGA_EVENTS`), as
 * data. Every point is ids, enums and numbers only: never a title, a message, a name, a path or an email.
 *
 * Analytics Engine has positional columns (`blob1..blob20`, `double1..double20`, `index1`). The layout below is shared
 * by every event type so one SQL query can span them; a column an event does not use is "" (blob) or 0 (double).
 * `index1` is the workspace id (Analytics Engine samples per index value, so one busy workspace never hides another).
 *
 * Add a column at the end only; never move or reuse one (three months of stored points keep the old meaning).
 */
import { Schema } from "effect"

/** What a point records (`blob1`). */
export const TELEMETRY_EVENTS = [
  "run_end",
  "turn_end",
  "usage",
  "card_opened",
  "card_resolved",
  "client_error",
  "client_vital",
  "client_action",
  "client_request_failed",
  "runner_report",
  /** Decision 0145: one point per part of a run's context (`kind` = `pack:…`, `history:…`, `system:…`, `step:…`). */
  "run_context",
  /**
   * Decision 0149: one point per resource metric of a run's end (`kind` = `cpu_peak`, `cpu_avg`, `rss_peak_mb`,
   * `machine_cpu_peak`, `machine_mem_peak`, `throttled`, `oom_kills`, `pressure`; `value`; `count` = samples).
   */
  "run_resources",
  /**
   * Decision 0150: a Platform Runner sandbox's ticks (`kind` = `window`: `count` ticks, `durationMs` mean round trip,
   * `value` the longest gap between two ticks, `bytes` the slowest round trip; `kind` = `slow`: one gap of 5 s or more
   * or an unanswered tick, `value` = the gap, `outcome` = ok or unanswered).
   */
  "platform_tick_latency",
  /**
   * Decision 0150: a Platform Runner lease's renewals on the Workspace DO (`kind` = `window`: `count`, `durationMs` mean,
   * `value` slowest; `kind` = `slow`: one renewal of 5 s or more, or one that lost its lease, `outcome` = ok or the loss).
   */
  "platform_renew_latency",
] as const
export type TelemetryEventName = (typeof TELEMETRY_EVENTS)[number]

/** The blob columns in order (`blob1` = event). */
export const TELEMETRY_BLOBS = [
  "event",
  "workspaceId",
  "projectId",
  "agentId",
  "runId",
  "correlationId",
  /** run kind, card type, vital or action name, error name, runner report kind */
  "kind",
  /** stop reason, card resolution, vital rating */
  "outcome",
  "errorCode",
  "provider",
  "model",
  /** billing class (usage), turn trigger (person | wake) */
  "billing",
  /** runner or CLI version, web build id */
  "version",
  /** route template (client events), runner kind (run_end: runner | platform | cloud) */
  "route",
  /** item id (runs), card id (cards) */
  "subjectId",
  "runnerId",
  /** funded kind (usage), card escalation target */
  "detail",
  /** user id (who asked, who decided): an id, never an email */
  "userId",
] as const
export type TelemetryBlob = (typeof TELEMETRY_BLOBS)[number]

/** The double columns in order. */
export const TELEMETRY_DOUBLES = [
  /** wall time, card latency, request duration */
  "durationMs",
  /** run: requested → leased / started */
  "queuedMs",
  /** tool calls, samples, attempts */
  "count",
  /** tool errors, failed checks */
  "errors",
  "inputTokens",
  "outputTokens",
  "costMicroUsd",
  "listMicroUsd",
  /** a vital's value, an HTTP status */
  "value",
  /** 1 when retried, else 0 */
  "retried",
  /** sizes (bytes) */
  "bytes",
  /** Decision 0145: cache reads (run_end, usage; the provider's count) */
  "cacheReadTokens",
  /** Decision 0145: cache writes (run_end, usage) */
  "cacheWriteTokens",
  /** Decision 0145: model calls (run_end, usage: the usage rows' `turns`) */
  "steps",
  /** Decision 0145: estimated tokens (run_context: bytes / 4, or the provider's per-call average for `step:…`) */
  "tokens",
] as const
export type TelemetryDouble = (typeof TELEMETRY_DOUBLES)[number]

export type TelemetryEvent = { readonly event: TelemetryEventName } & {
  readonly [K in Exclude<TelemetryBlob, "event">]?: string | null | undefined
} & { readonly [K in TelemetryDouble]?: number | null | undefined }

/** One token: ids, enums, versions, route templates. Anything else is written as "?" (never content). */
const TOKEN = /^[A-Za-z0-9_.:/@+=[\]$*-]{1,160}$/

/** Analytics Engine's `writeDataPoint` argument: `indexes: [workspaceId]`, the blobs and doubles in column order. */
export interface TelemetryDataPoint {
  readonly indexes: [string]
  readonly blobs: Array<string>
  readonly doubles: Array<number>
}

/** The point of an event (pure). Strings that are not one short token become "?"; non-finite numbers 0. */
export const telemetryPoint = (e: TelemetryEvent): TelemetryDataPoint => {
  const rec = e as unknown as Record<string, unknown>
  const blobs = TELEMETRY_BLOBS.map((k) => {
    const v = rec[k]
    if (v === undefined || v === null || v === "") return ""
    return typeof v === "string" && TOKEN.test(v) ? v : "?"
  })
  const doubles = TELEMETRY_DOUBLES.map((k) => {
    const v = rec[k]
    return typeof v === "number" && Number.isFinite(v) ? v : 0
  })
  const ws = blobs[1] ?? ""
  return { indexes: [ws === "" || ws === "?" ? "-" : ws.slice(0, 96)], blobs, doubles }
}

// #region runner-telemetry
/**
 * Decision 0143: what a runner reports about itself by default (0140 §9: errors and versions). Codes, ids, versions and
 * counts; `message` is a short error text the runner scrubs and the cloud scrubs again and keeps in its logs only.
 * Sent with HTTPS to `POST /api/runners/telemetry` (the runner's own token), only to a cloud whose Register reply
 * lists `runner_telemetry`; a runner that sends it announces the manifest feature `telemetry`.
 */
const Token = (max: number) =>
  Schema.String.check(Schema.isPattern(new RegExp(`^[A-Za-z0-9_.:/@+-]{1,${max}}$`)))

export const RunnerTelemetryEvent = Schema.Struct({
  /**
   * `version` (at start: runner and CLI versions), `run_trouble` (a run that did not end well here), `update_failed`,
   * `config_error`, `crash` (the previous process died; reported by the next one), `error` (anything else). Open: a
   * newer runner's kind is kept as is.
   */
  kind: Token(40),
  /** A stable code (`executor_crashed`, `prepare_failed`, `cli_error`, `ENOENT`). */
  code: Schema.optionalKey(Token(80)),
  runId: Schema.optionalKey(Token(80)),
  correlationId: Schema.optionalKey(Token(160)),
  profileId: Schema.optionalKey(Token(80)),
  /** `claude`, `codex`, … */
  cli: Schema.optionalKey(Token(40)),
  cliVersion: Schema.optionalKey(Token(80)),
  exitCode: Schema.optionalKey(Schema.Int),
  count: Schema.optionalKey(Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 1_000_000 }))),
  /** Scrubbed and cut to 200 characters on both sides; logs only, never Analytics Engine. */
  message: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(300))),
  at: Schema.Number.check(Schema.isFinite()),
})
export type RunnerTelemetryEvent = typeof RunnerTelemetryEvent.Type

export const RUNNER_TELEMETRY_MAX_EVENTS = 20
export const RUNNER_TELEMETRY_MAX_BYTES = 16 * 1024

export const RunnerTelemetryReport = Schema.Struct({
  v: Schema.Literal(1),
  runnerVersion: Token(40),
  os: Token(20),
  arch: Token(20),
  bun: Schema.optionalKey(Token(40)),
  events: Schema.Array(RunnerTelemetryEvent).check(Schema.isMaxLength(RUNNER_TELEMETRY_MAX_EVENTS)),
})
export type RunnerTelemetryReport = typeof RunnerTelemetryReport.Type
// #endregion runner-telemetry
